import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Tab,
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Grid
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import axios from 'axios';
import { Male, Female } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from 'src/apiConfig';
import {
  colors,
  FirstColumnCell,
  GenderButton,
  HeaderTableCell,
  StyledTableCell,
  StyledTableRow,
  StyledTabs
} from '../../helper';

/* STATISTIKA ZA ADMINA - STATISTIKA ZA SVAKI RAZRED PO ODGOVORIMA */

const AdminStatResult = () => {
  const [grade, setGrade] = useState(1);
  const [gender, setGender] = useState(null);
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedTab, setSelectedTab] = useState('1');
  const [correctAnswers, setCorrectAnswers] = useState({});

  const { t } = useTranslation();

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setGrade(newValue);
  };

  const handleGenderChange = (newGender) => {
    setGender(gender === newGender ? null : newGender);
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const genderParam = gender ? `&gender=${gender}` : '';

        // Fetčovanje statistika odgovora
        const statisticsPromise = axios.get(
          `${API_BASE_URL}/api/statistics.php?statistics=depend_result_by_class_and_gender&class=${grade}${genderParam}`
        );

        // Fetčovanje tačnih rešenja
        const solutionsPromise = axios.get(
          `${API_BASE_URL}/api/gradeSolutions.php`
        );

        // Čekanje da obe fetčovanja završe
        const [statResponse, solResponse] = await Promise.all([
          statisticsPromise,
          solutionsPromise
        ]);

        const results = statResponse.data.results;
        setData(results);

        // Pronalaženje rešenja za trenutni razred
        const gradeSolution = solResponse.data.find(
          (sol) => sol.grade == grade
        );
        if (!gradeSolution) {
          console.error(`Nema rešenja za razred ${grade}`);
          setChartData([]);
          return;
        }

        // Parsiranje rešenja u objekat { questionNumber: correctAnswer }
        const solutionsArray = gradeSolution.solutions
          .split(':')
          .filter((s) => s);
        const correctAnswers = {};
        solutionsArray.forEach((s) => {
          const questionNumber = s.match(/\d+/)[0]; // Ekstrakcija broja pitanja
          const answer = s.match(/[A-Z]/i)[0]; // Ekstrakcija odgovora (A, B, C, ...)
          correctAnswers[questionNumber] = answer;
        });

        // Priprema podataka za dijagram
        const chartDataPrepared = results.map((result) => {
          const questionNumber = result.question;
          const correctAnswer = correctAnswers[questionNumber];

          // Parsiranje rešenja u objekat { questionNumber: correctAnswer }
          const solutionsArray = gradeSolution.solutions
            .split(':')
            .filter((s) => s);
          const parsedCorrectAnswers = {};
          solutionsArray.forEach((s) => {
            const questionNumberMatch = s.match(/\d+/);
            const answerMatch = s.match(/[A-Z]/i);
            if (questionNumberMatch && answerMatch) {
              const questionNumber = questionNumberMatch[0];
              const answer = answerMatch[0].toUpperCase();
              parsedCorrectAnswers[questionNumber] = answer;
            }
          });

          setCorrectAnswers(parsedCorrectAnswers);

          const answerData = result[correctAnswer];
          let correctCount = 0;

          if (answerData) {
            const match = answerData.match(/\d+/);
            if (match) {
              correctCount = parseInt(match[0], 10);
            }
          }

          return {
            label: `${t('columns.question')} ${questionNumber}`,
            value: correctCount
          };
        });

        setChartData(chartDataPrepared);
      } catch (error) {
        console.error('Greška prilikom fetčovanja podataka:', error);
      }
    };

    fetchData();
  }, [grade, gender]);

  return (
    <Box sx={{ padding: '20px' }}>
      <AppBar
        position="static"
        color="default"
        sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
      >
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={t('results.tabsLabel')}
        >
          {[...Array(12).keys()].map((num) => (
            <Tab
              label={t('results.grade', { grade: num + 1 })}
              value={(num + 1).toString()}
              key={num}
              sx={{
                fontWeight:
                  selectedTab === (num + 1).toString() ? 'bold' : 'normal',
                backgroundColor:
                  selectedTab === (num + 1).toString() ? '#4facfe' : '#ffffff',
                color:
                  selectedTab === (num + 1).toString() ? '#ffffff' : '#333',
                '&:hover': {
                  backgroundColor:
                    selectedTab === (num + 1).toString() ? '#1c75bc' : '#f0f0f0'
                },
                borderRadius: '8px',
                transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s'
              }}
            />
          ))}
        </StyledTabs>
      </AppBar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '10px'
        }}
      >
        <GenderButton
          onClick={() => handleGenderChange('m')}
          selected={gender === 'm'}
        >
          <Male />
        </GenderButton>
        <GenderButton
          onClick={() => handleGenderChange('z')}
          selected={gender === 'z'}
        >
          <Female />
        </GenderButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={7}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              padding: '20px',
              backgroundColor: '#ffffff'
            }}
          >
            <Table>
              <TableHead>
                <StyledTableRow>
                  <HeaderTableCell sx={{ width: '10%' }}>
                    {t('columns.question')}
                  </HeaderTableCell>
                  <HeaderTableCell>{t('solutions.letters.A')}</HeaderTableCell>
                  <HeaderTableCell>{t('solutions.letters.B')}</HeaderTableCell>
                  <HeaderTableCell>{t('solutions.letters.V')}</HeaderTableCell>
                  <HeaderTableCell>{t('solutions.letters.G')}</HeaderTableCell>
                  <HeaderTableCell>{t('solutions.letters.D')}</HeaderTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => {
                  const correctAnswer = correctAnswers[row.question];
                  return (
                    <StyledTableRow key={index}>
                      <FirstColumnCell>{row.question}</FirstColumnCell>
                      <StyledTableCell
                        sx={{
                          backgroundColor:
                            correctAnswer === 'A'
                              ? '#0575051f' // Svetlo zelena
                              : 'inherit'
                        }}
                      >
                        {row.A}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          backgroundColor:
                            correctAnswer === 'B' ? '#0575051f' : 'inherit'
                        }}
                      >
                        {row.B}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          backgroundColor:
                            correctAnswer === 'V' ? '#0575051f' : 'inherit'
                        }}
                      >
                        {row.V}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          backgroundColor:
                            correctAnswer === 'G' ? '#0575051f' : 'inherit'
                        }}
                      >
                        {row.G}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          backgroundColor:
                            correctAnswer === 'D' ? '#0575051f' : 'inherit'
                        }}
                      >
                        {row.D}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={5}>
          <Box
            sx={{
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              padding: '20px',
              backgroundColor: '#F7F9FC',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <ResponsiveContainer width="100%" height={700}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={200}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminStatResult;
